import { ReactElement, useCallback, useState } from 'react';
import { Button, Card, CardContent } from '@mui/material';
import { LoadingFragment, LambdaCapacityProvidersTable, Dropdown, PageTitle, ButtonWithDialog } from '@localstack/ui';
import { useRoutes, useAwsGetter, useAwsEffect } from '@localstack/services';

import { LambdaProps } from './types';
import { NavTabs } from './components/NavTabs';

export const LambdaCapacityProviders = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { goto } = useRoutes();

  const {
    data: capacityProviders,
    isLoading: isCapacityProvidersLoading,
    mutate,
  } = useAwsGetter('Lambda', 'listCapacityProviders', [], { clientOverrides });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { deleteCapacityProvider } = useAwsEffect('Lambda', ['deleteCapacityProvider'], {
    revalidate: ['listCapacityProviders', 'getCapacityProvider'],
  });

  const handleDeleteSelected = useCallback(async () => {
    const promises = selectedIds.map((name) =>
      deleteCapacityProvider({ CapacityProviderName: name.split(':').pop() as string }),
    );
    await Promise.all(promises);
  }, [selectedIds]);

  return (
    <>
      <Layout
        title={
          <PageTitle
            title="Lambda Capacity Providers"
            onMutate={mutate}
            breadcrumbs={[
              ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
              ['Capacity Providers', () => null],
            ]}
          />
        }
        actions={
          <>
            <Button variant="outlined" onClick={() => goto(routes.RESOURCES_LAMBDA_CAPACITY_PROVIDER_NEW)}>
              Create
            </Button>
            <Dropdown label="Actions">
              <ButtonWithDialog
                componentType="MenuItem"
                disabled={selectedIds.length === 0 || isCapacityProvidersLoading}
                dialogTitle={`Remove ${selectedIds.length} Capacity Provider(s)?`}
                onClick={handleDeleteSelected}
                dialogContent="Selected Capacity Provider will be permanently deleted"
              >
                Remove Selected
              </ButtonWithDialog>
            </Dropdown>
          </>
        }
        tabs={<NavTabs routes={routes} />}
        documentTitle="Lambda: Capacity Providers"
      >
        <Card>
          <CardContent>
            <LoadingFragment
              loading={isCapacityProvidersLoading}
              arrayData={capacityProviders?.CapacityProviders ?? []}
              variant="list"
              height={50}
              size={10}
            >
              <LambdaCapacityProvidersTable
                loading={isCapacityProvidersLoading}
                capacityProviders={capacityProviders?.CapacityProviders ?? []}
                onView={(name) => goto(routes.RESOURCES_LAMBDA_CAPACITY_PROVIDER, { name })}
                onSelect={setSelectedIds}
              />
            </LoadingFragment>
          </CardContent>
        </Card>
      </Layout>
    </>
  );
};
